/**
 * AG-UI (Agent-User Interaction) 协议消息类型定义与解析器
 *
 * AG-UI 是一种 Agent 与用户之间 SSE 通信的标准协议（参考 CopilotKit AG-UI 规范）。
 * CrashSight 的后端在标准 AG-UI 基础上做了扩展，增加了 rawEvent 字段，
 * 其中包含 message_id 和 conversation_id 等业务信息。
 *
 * === 核心事件流程 ===
 * 一次完整的 AI 对话响应，SSE 事件流的典型顺序：
 *
 * RUN_STARTED (携带 conversationId)
 *   → THINKING_TEXT_MESSAGE_START → CONTENT (多次) → END  (思考过程，可多轮)
 *   → TEXT_MESSAGE_START → CONTENT (多次) → END            (AI 文本回复)
 *   → TOOL_CALL_START → ARGS → END → RESULT               (工具调用)
 *   → (可能交替出现多轮 THINKING + TEXT + TOOL_CALL)
 * → RUN_FINISHED
 *
 * === 使用方式 ===
 * 在 SSE 的 onmessage 回调中：
 *   1. 调用 parseAgUiEvent(e.data) 解析 JSON 为事件对象
 *   2. 根据 event.type 做 switch-case 分类处理
 *   3. 通过 extractConversationId(event) 获取 conversationId（仅部分事件携带）
 */
// ============================================================
// 事件类型枚举
// ============================================================
export var AgUiEventType;
(function (AgUiEventType) {
    // --- 运行生命周期 ---
    /** SSE 流开始，通常在 rawEvent 中携带 conversation_id 和 message_id */
    AgUiEventType["RUN_STARTED"] = "RUN_STARTED";
    /** SSE 流正常结束 */
    AgUiEventType["RUN_FINISHED"] = "RUN_FINISHED";
    /** SSE 流异常结束 */
    AgUiEventType["RUN_ERROR"] = "RUN_ERROR";
    // --- 文本消息（AI 回复的流式文本）---
    /** AI 回复开始 */
    AgUiEventType["TEXT_MESSAGE_START"] = "TEXT_MESSAGE_START";
    /** AI 回复的流式文本片段，通过 delta 字段逐步输出 */
    AgUiEventType["TEXT_MESSAGE_CONTENT"] = "TEXT_MESSAGE_CONTENT";
    /** AI 回复结束 */
    AgUiEventType["TEXT_MESSAGE_END"] = "TEXT_MESSAGE_END";
    // --- 工具调用（AI 调用外部工具，如查询崩溃数据等）---
    /** 工具调用开始，携带 toolCallName 等信息 */
    AgUiEventType["TOOL_CALL_START"] = "TOOL_CALL_START";
    /** 工具调用参数（流式输出） */
    AgUiEventType["TOOL_CALL_ARGS"] = "TOOL_CALL_ARGS";
    /** 工具调用完成 */
    AgUiEventType["TOOL_CALL_END"] = "TOOL_CALL_END";
    /** 工具调用结果（标准 AG-UI 事件，携带工具执行返回值） */
    AgUiEventType["TOOL_CALL_RESULT"] = "TOOL_CALL_RESULT";
    // --- 思考过程（AG-UI deprecated THINKING_* 事件，后端实际使用此系列）---
    /** 思考过程开始 */
    AgUiEventType["THINKING_TEXT_MESSAGE_START"] = "THINKING_TEXT_MESSAGE_START";
    /** 思考过程流式文本片段，通过 delta 字段逐步输出 */
    AgUiEventType["THINKING_TEXT_MESSAGE_CONTENT"] = "THINKING_TEXT_MESSAGE_CONTENT";
    /** 思考过程结束 */
    AgUiEventType["THINKING_TEXT_MESSAGE_END"] = "THINKING_TEXT_MESSAGE_END";
    /** CrashClaw 扩展：Bridge 读取 SEND_HTML 文件后下发，前端以 iframe 卡片展示（旧协议，兼容保留） */
    AgUiEventType["CRASHCLAW_HTML_PREVIEW"] = "CRASHCLAW_HTML_PREVIEW";
    /** CrashClaw 扩展：Bridge 读取 SEND_FILE 文件后下发，前端根据 mime 类型渲染卡片 */
    AgUiEventType["CRASHCLAW_FILE_DELIVERY"] = "CRASHCLAW_FILE_DELIVERY";
    // --- 步骤事件 ---
    AgUiEventType["STEP_STARTED"] = "STEP_STARTED";
    AgUiEventType["STEP_FINISHED"] = "STEP_FINISHED";
})(AgUiEventType || (AgUiEventType = {}));
// ============================================================
// 解析与工具函数
// ============================================================
/**
 * 解析 SSE 消息的 data 字段为 AG-UI 事件对象
 * @param data - SSE event.data 字符串（JSON 格式）
 * @returns 解析后的事件对象，解析失败返回 null
 */
export function parseAgUiEvent(data) {
    if (!data)
        return null;
    try {
        return JSON.parse(data);
    }
    catch (e) {
        console.warn('[AG-UI] Failed to parse event:', data, e);
        return null;
    }
}
/**
 * 从事件的 rawEvent 扩展字段中提取 conversationId
 * 注意：不是每个事件都有 rawEvent，调用方需处理 undefined 返回值
 */
export function extractConversationId(event) {
    return event?.rawEvent?.conversation_id || event?.threadId;
}
/**
 * 从事件的 rawEvent 扩展字段中提取 messageId
 */
export function extractMessageId(event) {
    return event?.rawEvent?.message_id;
}
